import React, { useState } from 'react';
import { ChevronRight, CheckCircle2, ArrowRight, Cog, Factory, Droplets, Wind, Train, Building2, Layers, Sparkles, Fan, Settings, CircleDot } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';

const serviceIcons = {
  train: Train,
  crane: Building2,
  conveyor: Cog,
  building: Building2,
  factory: Factory,
  droplets: Droplets,
  wind: Wind,
  fan: Fan,
  layers: Layers,
  sparkles: Sparkles,
  beam: Settings,
  wheel: CircleDot,
  cog: Cog,
  rail: Cog
};

const Services = () => {
  const [activeService, setActiveService] = useState(services[0].id);

  const selectedService = services.find(s => s.id === activeService);

  return (
    <div className="bg-[#0b0b0b]">
      {/* Hero Section */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px),
                             repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(255,255,255,0.03) 50px, rgba(255,255,255,0.03) 51px)`
          }} />
        </div>
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#b00000] via-red-600 to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#b00000] text-xs sm:text-sm font-medium tracking-widest uppercase mb-3 sm:mb-4 block">Our Services</span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Comprehensive CHP Solutions
            </h1>
            <p className="text-base sm:text-lg text-white/70 leading-relaxed">
              From Wagon Tipplers to FGD systems, we provide complete operation, maintenance, 
              structural restoration, and manufacturing services for thermal power plants across India.
            </p>
          </div>
        </div>
      </section>

      {/* Services Interactive Section */}
      <section className="py-12 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Service List */}
            <div className="lg:col-span-1 space-y-2 max-h-[300px] lg:max-h-[800px] overflow-y-auto pr-2 order-1">
              {services.map((service) => {
                const IconComponent = serviceIcons[service.icon] || Cog;
                const isActive = activeService === service.id;
                
                return (
                  <button
                    key={service.id}
                    onClick={() => setActiveService(service.id)}
                    className={`w-full text-left p-3 sm:p-4 border transition-all duration-300 flex items-center gap-3 sm:gap-4 group ${
                      isActive 
                        ? 'bg-[#b00000]/20 border-[#b00000]' 
                        : 'bg-[#0b0b0b] border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className={`w-8 sm:w-10 h-8 sm:h-10 flex items-center justify-center transition-colors flex-shrink-0 ${
                      isActive ? 'bg-[#b00000]' : 'bg-[#0b0b0b] group-hover:bg-white/20'
                    }`}>
                      <IconComponent className={`w-4 sm:w-5 h-4 sm:h-5 ${isActive ? 'text-white' : 'text-white/60'}`} />
                    </div>
                    <span className={`flex-1 text-xs sm:text-sm font-medium ${isActive ? 'text-white' : 'text-white/70'}`}>
                      {service.title}
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform flex-shrink-0 ${isActive ? 'text-[#b00000] rotate-90' : 'text-white/40'}`} />
                  </button>
                );
              })}
            </div>

            {/* Service Details */}
            <div className="lg:col-span-2 order-2">
              {selectedService && (
                <div className="bg-[#0b0b0b] border border-white/10 lg:sticky lg:top-24">
                  {/* Service Image - Only show if image exists */}
                  {selectedService.image && (
                    <div className="w-full h-[200px] sm:h-[300px] lg:h-[400px] overflow-hidden">
                      <img 
                        src={selectedService.image} 
                        alt={selectedService.title}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>
                  )}
                  
                  <div className="p-4 sm:p-6 lg:p-8">
                    <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                      {(() => {
                        const IconComponent = serviceIcons[selectedService.icon] || Cog;
                        return (
                          <div className="w-12 sm:w-16 h-12 sm:h-16 bg-[#b00000] flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 sm:w-8 h-6 sm:h-8 text-white" />
                          </div>
                        );
                      })()}
                      <div>
                        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">{selectedService.title}</h2>
                      </div>
                    </div>
                    
                    <p className="text-white/70 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base">
                      {selectedService.fullDesc}
                    </p>

                    <h3 className="text-white font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Key Features & Capabilities</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8">
                      {selectedService.features.map((feature, index) => (
                        <div key={index} className="flex items-center gap-2 sm:gap-3 bg-[#0b0b0b] p-2 sm:p-3">
                          <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 text-[#b00000] flex-shrink-0" />
                          <span className="text-white/80 text-xs sm:text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/contact">
                      <Button className="w-full sm:w-auto bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-6 h-11 sm:h-12 transition-all duration-300 group">
                        Request Service Quote
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* All Services Grid */}
      <section className="py-20 border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Complete Service Portfolio
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              All 14 specialized services for Coal Handling Plants and Thermal Power Stations.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {services.map((service) => {
              const IconComponent = serviceIcons[service.icon] || Cog;
              return (
                <div 
                  key={service.id}
                  onClick={() => {
                    setActiveService(service.id);
                    window.scrollTo({ top: 300, behavior: 'smooth' });
                  }}
                  className="bg-[#0b0b0b] border border-white/10 p-5 hover:border-[#b00000]/50 transition-all duration-300 cursor-pointer group"
                >
                  <div className="w-10 h-10 bg-[#b00000]/20 flex items-center justify-center mb-3 group-hover:bg-[#b00000] transition-colors">
                    <IconComponent className="w-5 h-5 text-[#b00000] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-white font-medium text-sm mb-1">{service.title}</h3>
                  <p className="text-white/50 text-xs line-clamp-2">{service.shortDesc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#b00000] text-sm font-medium tracking-widest uppercase mb-4 block">Service Categories</span>
            <h2 className="text-3xl font-bold text-white mb-4">Areas of Expertise</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Operation & Maintenance', services: ['Wagon Tippler O&M', 'Stacker Reclaimer O&M', 'Complete CHP Maintenance'], count: 3 },
              { title: 'Structural Work', services: ['CHP Restoration', 'Side Beam Work', 'Rail Restrengthening'], count: 3 },
              { title: 'Manufacturing & Repair', services: ['Drive Bogie', 'Support Wheel', 'Hydraulic Systems'], count: 3 },
              { title: 'Specialized Services', services: ['FGD Maintenance', 'Air Ventilation', 'Plant Housekeeping'], count: 3 }
            ].map((category, index) => (
              <div key={index} className="bg-[#0b0b0b] border border-white/10 p-6 hover:border-[#b00000]/50 transition-colors">
                <h3 className="text-white font-semibold mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.services.map((service, idx) => (
                    <li key={idx} className="text-white/60 text-sm flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#b00000]" />
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 border-t border-white/10 bg-gradient-to-r from-[#b00000]/10 to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Need Custom Engineering Solutions?
          </h2>
          <p className="text-white/60 max-w-2xl mx-auto mb-8">
            Our team of experts with 35+ years of experience is ready to develop tailored solutions 
            for your specific Coal Handling Plant requirements.
          </p>
          <Link to="/contact">
            <Button className="bg-[#FFD700] hover:bg-[#FFC000] text-black font-semibold rounded-none none px-8 h-14 text-base transition-all duration-300 group">
              Contact Our Experts
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
